import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Popover, Dropdown } from 'antd'
import { SearchOutlined, CloseOutlined } from '@ant-design/icons'
import { handleErrorCatchLog, renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { setCapitalLetter } from '../../../Helpers/StringHelpers'
import PriceSlider from '../../Generic/PriceSlider'
import { addPriceFilter, setActiveDropdown, setActivePriceDropdown } from '../../../Slices/Kiosk/TableOptionsSlice'
import { populateTableWithArticles, getPriceListLimits } from '../../../Services/Kiosk/ArticleServices'
import { TableColumnTranslationPrefix } from '../../../Constants/Configuration'
import { useAppDispatch, useAppSelector } from '../../../hooks'

function LocalizedSpan(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const pricelists = useAppSelector(state => state.present.tableOptions.pricelists)
  const priceFilter = useAppSelector(state => state.present.tableOptions.priceFilter)
  const emptyExtraDescriptions = useAppSelector(state => state.present.tableOptions.emptyExtraDescriptions)
  const negateTableColumns = useAppSelector(state => state.present.tableOptions.negateTableColumns)
  const tableFilters = useAppSelector(state => state.present.tableOptions.tableFilters)
  const filterByAvailability = useAppSelector(state => state.present.tableOptions.filterByAvailability)
  const filterSearchCurrentValue = useAppSelector(state => state.present.tableOptions.filterSearchCurrentValue)
  const activeDropdown = useAppSelector(state => state.present.tableOptions.activeDropdown)
  const activePriceDropdown = useAppSelector(state => state.present.tableOptions.activePriceDropdown)

  const pricelistName = useMemo(() => {
    return props.pricelist === 1 ? pricelists.priceList1 : props.pricelist === 2 ? pricelists.priceList2 : undefined
  }, [pricelists, props.pricelist])

  const [minPrice, setMinPrice] = useState(Math.floor(1 / 10) * 10)
  const [maxPrice, setMaxPrice] = useState(Math.ceil(5000 / 10) * 10)

  const [column, pricelist]: [string, number] = useMemo(() => {
    if (activePriceDropdown) {
      const columnAndPricelist = activePriceDropdown.split('|')
      return [columnAndPricelist[0], parseInt(columnAndPricelist[1])]
    }
    return ['', 0]
  }, [activePriceDropdown])

  const hideDropdown = useCallback(() => {
    dispatch(setActivePriceDropdown(''))
  }, [dispatch])

  const filterAndHideDropdown = useCallback(
    (min, max) => {
      dispatch(setActivePriceDropdown(''))
      dispatch(
        addPriceFilter({
          pricelist: pricelistName,
          field: props.languageKey,
          min,
          max,
        }),
      )
      dispatch(populateTableWithArticles())
    },
    [pricelistName, dispatch, props.languageKey],
  )

  const removeFilter = useCallback(() => {
    dispatch(addPriceFilter({}))
    dispatch(populateTableWithArticles())
  }, [dispatch])

  useEffect(() => {
    if (activePriceDropdown) {
      window.addEventListener('click', hideDropdown)
    } else {
      window.removeEventListener('click', hideDropdown)
    }
    return () => {
      window.removeEventListener('click', hideDropdown)
    }
  }, [activePriceDropdown, hideDropdown])

  const columnFiltered = useMemo(() => {
    return priceFilter && priceFilter.pricelist === pricelistName && priceFilter.field === props.languageKey
  }, [priceFilter, pricelistName, props.languageKey])

  useEffect(() => {
    getPriceListLimits(pricelistName, props.languageKey)
      .then(res => {
        setMinPrice(Math.floor(res[0] / 10) * 10)
        setMaxPrice(Math.ceil(res[1] / 10) * 10)
      })
      .catch(err => {
        handleErrorCatchLog(err, 'Failed retrieve price limits getPriceListLimits')
        console.log(err)
      })
  }, [
    pricelistName,
    props.languageKey,
    tableFilters,
    negateTableColumns,
    emptyExtraDescriptions,
    filterByAvailability,
    filterSearchCurrentValue,
  ])

  useEffect(() => {
    if (activeDropdown) {
      dispatch(setActivePriceDropdown(''))
    }
  }, [activeDropdown, dispatch])

  const showPriceSlider = activePriceDropdown !== '' && column === props.languageKey && pricelist === props.pricelist

  return (
    <Dropdown
      className='additional-menu-dropdown'
      menu={{
        onClick: ({ domEvent }) => stopPropagation(domEvent),
        items: [
          {
            key: props.languageKey,
            label: <PriceSlider onClose={filterAndHideDropdown} minPrice={minPrice} maxPrice={maxPrice} />,
          },
        ],
      }}
      open={showPriceSlider}
    >
      {columnFiltered ? (
        <div className='complete-search-label truncate-text'>
          <span className='clear-search-icon' onClick={removeFilter}>
            <CloseOutlined className='general-clear-icon cursor-pointer' />
          </span>{' '}
          {priceFilter.min} - {priceFilter.max}
        </div>
      ) : (
        <Popover content={pricelistName} trigger='hover'>
          <div
            className='complete-search-label truncate-text'
            onClick={e => {
              e.stopPropagation()
              dispatch(setActivePriceDropdown(props.languageKey + '|' + props.pricelist))
              dispatch(setActiveDropdown(''))
            }}
          >
            {renderTranslation(
              language,
              TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(props.languageKey)],
            )}{' '}
            <SearchOutlined className='pl-2' />
          </div>
        </Popover>
      )}
    </Dropdown>
  )
}
export default LocalizedSpan
